import {calcUserScore, filterObjectByParams} from 'helpers/common-helper';
import Player from './player';
import Const from 'constants';
import Action from 'actions';
import {getDefaultExposure} from 'helpers/player-helper';
import {getPlayerModeProjOwnFunctionality} from 'helpers/projected-ownership';
import {toFloat} from 'helpers/number-helper';

export default class PlayerShowdown extends Player {
    preparePoolsOfPlayers({incomingValues: {players, filterPlayersBeforePreparingPools}}) {
        if (!filterPlayersBeforePreparingPools) {
            return this.exec('createPoolsOfPlayers', {
                players: Object.values(players),
            });
        }

        const {
            playersData: {filters},
        } = Action.getState();

        const params = {
            team: {
                filterType: Const.FILTER_TYPE_IN_ARRAY,
                filterValue: filters.games,
            },
        };

        players = filterObjectByParams(players, params, true);

        return this.exec('createPoolsOfPlayers', {
            players,
        });
    }

    resetPoints({incomingValues: {dispatch}}) {
        return this.exec('resetPoints_ShowDown', {dispatch});
    }

    resetSettings({incomingValues: {dispatch}}) {
        return this.exec('resetSettings_ShowDown', {dispatch});
    }

    createPoolsOfPlayers({incomingValues: {players}}) {
        return this.exec('createPoolsOfPlayers_ShowDown', {players});
    }

    getExpandedEntityParams({incomingValues = {}}) {
        const {modesConfig} = Action.getState();
        const expandedPlayer = super.getExpandedEntityParams({incomingValues});
        const sharpPoints = toFloat(expandedPlayer.sharpPoints ?? 0, 3);
        const cptPointCoef = modesConfig.getConfig('CPT').getPointCoefficient();
        const captainUserPoints = expandedPlayer.userPoints * cptPointCoef;
        const captainSharpPoints = sharpPoints * cptPointCoef;
        const captainAdminPoints = expandedPlayer.adminPoints * cptPointCoef;
        const captainCeilingPoints = expandedPlayer.ceilingPoints * cptPointCoef;
        const captainPoints = expandedPlayer.points * cptPointCoef;
        const captainSalary = expandedPlayer.salary * cptPointCoef;
        const captainFantasyPoints = expandedPlayer.fantasyPoints * cptPointCoef;
        const PlayerService = this;

        return {
            ...expandedPlayer,
            overall_exposureMax: getDefaultExposure('max'),
            sharpPoints,
            lineupRole: Const.DEFAULT_MODE,
            captain_fantasyPoints: toFloat(captainFantasyPoints, 3),
            captain_exposureMax: expandedPlayer.exposureMax,
            captain_exposureMin: expandedPlayer.exposureMin,
            captain_points: toFloat(captainPoints, 3),
            captain_adminPoints: toFloat(captainAdminPoints, 3),
            captain_sharpPoints: toFloat(captainSharpPoints, 3),
            captain_ceilingPoints: toFloat(captainCeilingPoints, 3),
            captain_userPoints: toFloat(captainUserPoints, 3),
            captain_userScore: calcUserScore(captainUserPoints, captainSalary, this.userScoreMultiplier),
            captain_salary: captainSalary,
            captain_pointsChangedManually: false,
            projOwn: {...getPlayerModeProjOwnFunctionality(expandedPlayer, ['cpt', 'flex'])},
            resetExposure() {
                this.overall_exposureMax = getDefaultExposure('max');
                this.exposureMin = getDefaultExposure('min');
                this.exposureMax = getDefaultExposure('max');
                this.captain_exposureMin = getDefaultExposure('min');
                this.captain_exposureMax = getDefaultExposure('max');
            },
            getPropNameOfMode(prop, incomingMode) {
                const mode = incomingMode || this.mode;

                return PlayerService.exec('definePropNameOfMode', {prop, mode});
            },
            getPropNameOfLineupRole(prop) {
                return this.getIsCaptain() ? this.getCapPropName(prop) : prop;
            },
            getPropValueOfLineupRole(prop) {
                if (prop === 'projected_ownership') {
                    return this.projOwn.getValue(this.getIsCaptain() ? Const.CAPTAIN_MODE : Const.DEFAULT_MODE);
                }

                let value,
                    propName = this.getPropNameOfLineupRole(prop);

                if (this.hasOwnProperty(propName)) {
                    value = this[propName];
                } else if (this.hasOwnProperty(prop)) {
                    value = this[prop];
                }

                return value;
            },
            getCapPropName(prop) {
                const mode = this.getIsCaptain() ? Const.CAPTAIN_MODE : Const.DEFAULT_MODE;

                return PlayerService.exec('definePropNameOfMode', {prop, mode});
            },
            getCapPropValue(prop) {
                if (prop === 'projected_ownership') {
                    return this.projOwn.getValue(Const.CAPTAIN_MODE);
                }

                let value,
                    propName = this.getCapPropName(prop);

                if (this.hasOwnProperty(propName)) {
                    value = this[propName];
                } else if (this.hasOwnProperty(prop)) {
                    value = this[prop];
                }

                return value;
            },
            getIsCaptain() {
                return this.lineupRole === Const.CAPTAIN_MODE;
            },
            setAsCaptain() {
                this.lineupRole = Const.CAPTAIN_MODE;
            },
            getLineupRole() {
                return this.lineupRole;
            },
            isFitForLineup(substitutablePlayer) {
                return true;
            },
        };
    }

    definePropNameOfMode({incomingValues: {prop, mode}}) {
        const captainPropsList = [
            'exposureMax',
            'exposureMin',
            'points',
            'adminPoints',
            'sharpPoints',
            'ceilingPoints',
            'userPoints',
            'userScore',
            'salary',
            'fantasyPoints',
            'pointsChangedManually',
        ];
        const overallPropsList = ['exposureMax'];

        if (Const.CAPTAIN_MODE === mode && inArray(prop, captainPropsList)) {
            return 'captain_' + prop;
        } else if (Const.OVERALL_MODE === mode && inArray(prop, overallPropsList)) {
            return 'overall_' + prop;
        }

        return prop;
    }

    afterGettingPlayers({incomingValues: {dispatch, initial = true}}) {
        dispatch(Action.do('unsetTeamDetails'));
    }

    loadExtraPlayerDataFromStore({incomingValues: {dispatch, data}}) {
        if (data.hasOwnProperty('cptPositionStacking')) {
            dispatch(Action.set('CptPositionStackingFromStorage', data.cptPositionStacking));
        }

        if (data.hasOwnProperty('stackQuarterbackWithPassReceivers')) {
            dispatch(Action.set('StackQuarterbackWithPassReceiversFromStorage', data.stackQuarterbackWithPassReceivers));
        }

        if (data.hasOwnProperty('allowOnlyOneKPositionPerLineup')) {
            dispatch(Action.set('AllowOnlyOneKPositionPerLineupFromStorage', data.allowOnlyOneKPositionPerLineup));
        }

        if (data.hasOwnProperty('allowOnlyOneDSTPositionPerLineup')) {
            dispatch(Action.set('AllowOnlyOneDSTPositionPerLineupFromStorage', data.allowOnlyOneDSTPositionPerLineup));
        }

        return this.exec('loadExtraPlayerDataFromStore_ShowDown', {dispatch, data});
    }

    getExtraPlayerDataToStore({...rest}) {
        return this.exec('getExtraPlayerDataToStore_ShowDown', {});
    }

    preparePlayerDataForDownloading({incomingValues: {player}}) {
        return this.exec('preparePlayerDataForDownloading_ShowDown', {player});
    }

    updatePlayersAfterRefreshingProjections({incomingValues: {indexedPlayers}}) {
        return this.exec('updatePlayersAfterRefreshingProjections_ShowDown', {indexedPlayers});
    }

    applySympathy({incomingValues: {players, playersSympathy}}) {
        return this.exec('applySympathy_ShowDown', {players, playersSympathy});
    }

    preparePlayerForLineup({incomingValues = {}}) {
        return this.exec('preparePlayerForLineup_ShowDown', incomingValues);
    }

    prepareRandomPoints({incomingValues: {}}) {
        return this.exec('prepareRandomPointsByModes', {});
    }
}
